<script>
    import { onMount, beforeUpdate } from "svelte";
    import { Chart, registerables } from "chart.js";
    import annotationPlugin from "chartjs-plugin-annotation";
    import { theme } from "$stores/themeStore";
    import { get } from 'svelte/store';

    export let data = [];

    let canvas;
    let chart;
    let chartBorderColor = "rgba(75, 192, 192, 1)";
    let chartBackgroundColor = "rgba(75, 192, 192, 0.8)";

    // Number of bars to display at a time
    const visibleBars = 224;

    // Calculate the width of the canvas based on the number of data items
    const barWidth = 10; // Width of each bar in pixels
    const canvasWidth = data.length * barWidth; // Total canvas width

    const lightModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "black",
                },
                ticks: {
                    color: "black",
                },
                min: 0, // Start at the first data point
                max: visibleBars - 1, // Show only 168 bars at a time
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "black",
                },
                position: "left",
                ticks: {
                    color: "black",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: 4,
                        yMax: 4,
                        borderColor: "black",
                        borderWidth: 0.5,
                    },
                },
            },
        },
    };

    const darkModeOptions = {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                title: {
                    display: false,
                    text: "Serial Number",
                    color: "white",
                },
                ticks: {
                    color: "white",
                },
                min: 0, // Start at the first data point
                max: visibleBars - 1, // Show only 168 bars at a time
            },
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: "Count",
                    color: "white",
                },
                position: "left",
                ticks: {
                    color: "white",
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            canvas: {
                responsive: true,
                maintainAspectRatio: false,
                resizeDelay: 1000,
            },
            annotation: {
                annotations: {
                    line1: {
                        type: "line",
                        yMin: 4,
                        yMax: 4,
                        borderColor: "rgba(110, 210, 170, 0.8)",
                        borderWidth: 0.5,
                    },
                },
            },
        },
    };

    onMount(() => {
        // Validate data
        if (!Array.isArray(data) || data.length === 0) {
            console.error("BarChart received invalid data: ", data.length);
            return;
        }

        Chart.register(...registerables, annotationPlugin);
        createChart();
    });

    beforeUpdate(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.8)" : "rgba(190, 24, 123, 0.8)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });

    $: if (chart) {
        chart.destroy();
        chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.8)" : "rgba(190, 24, 123, 0.8)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
        createChart();
    }

    function createChart() {
        const currentTheme = get(theme); // Get the current theme
        const options =
            currentTheme === "dark" ? darkModeOptions : lightModeOptions;

        const ctx = canvas.getContext("2d");

        chart = new Chart(ctx, {
            type: "bar",
            data: {
                labels: data.map((item) => item.day),
                datasets: [
                    {
                        label: "Count",
                        data: data.map((item) => item.count),
                        backgroundColor: chartBackgroundColor,
                        borderColor: chartBorderColor,
                        borderWidth: 1,
                    },
                ],
            },
            options: options,
        });
    }

    theme.subscribe(() => {
        if (chart) {
            chart.destroy();
            chartBackgroundColor = get(theme) === "dark" ? "rgba(13, 148, 136, 0.2)" : "rgba(190, 24, 123, 0.2)";
            chartBorderColor = get(theme) === "dark" ? "rgba(13, 148, 136, 1)" : "rgba(190, 24, 123, 1)";   
            createChart();
        }
    });
</script>

<div style="overflow-x: auto; width: 100%; height: 250px; scrollbar-width: none; -ms-overflow-style: none;">
    <style>
        div::-webkit-scrollbar {
            display: none;
        }
    </style>
    <canvas bind:this={canvas} width={canvasWidth} height="250"></canvas>
</div>
